import moment from "moment";
import { Badge, BadgeSize, BadgeVariant, Show } from "shared/components";
import { cx, toMoney } from "shared/utils";
import { CopyIconButton } from "shared/components/CopyIconButton";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { BankDeposit } from "../../../types/verified/bank";
import { SimpleTable } from "./PaystubTable/components/SimpleTable";

const depositDollars = ({ amount }: BankDeposit) =>
  typeof amount === "number" ? amount : amount.cents / 100;

const averageAmount = (deposits: BankDeposit[]) =>
  deposits.reduce((sum, deposit) => sum + depositDollars(deposit), 0) /
  deposits.length;

const percentFromAverage = (amount: number, average: number) =>
  average > 0 ? Math.round(((amount - average) / average) * 100) : null;

const describeDifference = (percent: number) => {
  if (percent === 0) return "Same as average";
  const direction = percent > 0 ? "above" : "below";
  return `${Math.abs(percent)}% ${direction} average`;
};

const OutlierCell = ({
  deposit,
  average
}: {
  deposit: BankDeposit;
  average: number;
}) => {
  const percent = percentFromAverage(depositDollars(deposit), average);
  const difference = percent === null ? null : describeDifference(percent);

  return deposit.outlier?.is_outlier ? (
    <span className="inline-flex">
      <Badge
        variant={BadgeVariant.WARNING}
        size={BadgeSize.SM}
        borderless={false}
      >
        {difference ? `Outlier · ${difference}` : "Outlier"}
      </Badge>
    </span>
  ) : (
    <Show if={difference}>
      <span className="text-xs text-content-300">{difference}</span>
    </Show>
  );
};

const accountLabel = ({ name, mask }: BankDeposit["account"]) =>
  [name, mask ? `••${mask}` : null].filter(Boolean).join(" ");

export const BankSourceDeposits = ({
  deposits
}: {
  deposits: BankDeposit[];
}) => {
  const average = averageAmount(deposits);

  return (
    <ContentTitle
      title={`Deposits (${deposits.length})`}
      tooltip="Every deposit from the applicant's linked bank account that makes up this income source, newest first, as the bank reported it."
      contentClassName="py-0"
    >
      <div className="space-y-2 pt-4">
        <KeyValue
          gapClassName="gap-6"
          label="Average deposit"
          value={toMoney(average)}
        />
      </div>
      <div className="max-h-[456px] overflow-y-auto">
        <SimpleTable<BankDeposit>
          data={deposits}
          rowKey={(deposit, idx) => deposit.transaction_id || idx}
          columns={[
            {
              label: "Description",
              render: (deposit) => (
                <div className="min-w-0">
                  <p className="truncate text-content-1000">
                    {deposit.original_description || "-"}
                  </p>
                  <div className="flex min-w-0 items-center gap-1">
                    <p className="truncate text-xs text-content-300">
                      {accountLabel(deposit.account)}
                    </p>
                    <Show if={deposit.transaction_id}>
                      <CopyIconButton
                        value={deposit.transaction_id ?? ""}
                        idleAriaLabel="Copy transaction ID"
                        size={12}
                      />
                    </Show>
                  </div>
                </div>
              ),
              className: "w-[40%]"
            },
            {
              label: "Date",
              render: (deposit) =>
                deposit.date ? moment(deposit.date).format("ll") : "-",
              className: "w-[15%]"
            },
            {
              label: "Amount",
              render: (deposit) => {
                const dollars = depositDollars(deposit);
                return (
                  <p
                    className={cx(
                      dollars >= 0 ? "text-success-700" : "text-danger-700"
                    )}
                  >
                    {dollars >= 0 ? "+ " : "- "}
                    {toMoney(Math.abs(dollars))}
                  </p>
                );
              },
              className: "w-[15%] text-right"
            },
            {
              label: "Compared to average",
              render: (deposit) => (
                <OutlierCell deposit={deposit} average={average} />
              ),
              className: "pl-6"
            }
          ]}
        />
      </div>
    </ContentTitle>
  );
};
